import { UseFormRegister, UseFormWatch } from "react-hook-form";
import { getFontAwesomeIcon } from "../../../../../../utils";
import { FormValues } from "./ApprovalFlowForm";
import { BaseToolTip } from "../../../../../../components";

interface ApprovalStageCardProps {
	stage: any;
	register: UseFormRegister<any>;
	setSelectedStage: (stage: any) => void;
	index: number;
	getValues: () => FormValues;
	watch: UseFormWatch<FormValues>;
	isStageApprovalEnabled: boolean;
}

// Reusable SVG Icons
const CheckIcon = () => (
	<svg
		className="w-7 h-7"
		fill="none"
		stroke="currentColor"
		viewBox="0 0 24 24"
	>
		<path
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth="3"
			d="M5 13l4 4L19 7"
		/>
	</svg>
);

const XIcon = () => (
	<svg
		className="w-7 h-7"
		fill="none"
		stroke="currentColor"
		viewBox="0 0 24 24"
	>
		<path
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth="3"
			d="M6 18L18 6M6 6l12 12"
		/>
	</svg>
);

const ApprovalStageCard: React.FC<ApprovalStageCardProps> = ({
	stage,
	register,
	setSelectedStage,
	index,
	getValues,
	watch,
	isStageApprovalEnabled,
}) => {
	const actionButtonValue =
		watch(`${stage.valueKey}.actionButtonSelected`) || null;
	const assignedToValue = watch(`${stage.valueKey}.assignedTo`);
	const assignedToName = assignedToValue?.name
		?.replace(/\s*\(.*?\)\s*$/, "")
		.trim();
	const stageAssignedUser = stage.assignedUser.name;

	// Centralized status calculation
	const orderDecisionIsMade = stage?.decision.isApproved !== null;
	const isOrderPreApproved = stage?.isPreApproved && stage?.valueKey === "2";

	const approvalDecisionHasBeenMade =
		orderDecisionIsMade && stage?.decision.isApproved === "1";
	const deniedDecisionHasBeenMade =
		orderDecisionIsMade && stage?.decision.isApproved === "0";

	const isPendingDecisionApproved = actionButtonValue === "1";
	const isPendingDecisionDenied = actionButtonValue === "0";
	const isPendingDecisionEmpty = stage?.decision.isApproved === null;

	const baseButtonClasses =
		"size-5 rounded-sm border-1 border-gray-300 bg-gray-100 flex items-center justify-center transition-all hover:border-gray-400 text-gray-400";
	// Stage Indicator Component

	const StageIndicator = () => {
		if (
			isOrderPreApproved ||
			isPendingDecisionApproved ||
			approvalDecisionHasBeenMade
		) {
			return (
				<span className="flex items-center justify-center size-8 bg-mint-700 rounded-full text-white">
					{getFontAwesomeIcon("check", "regular")}
				</span>
			);
		}
		if (isPendingDecisionDenied) {
			return (
				<span className="flex items-center justify-center size-8 bg-crimson-700 rounded-full text-white">
					{getFontAwesomeIcon("xmark", "regular")}
				</span>
			);
		}
		if (isPendingDecisionEmpty) {
			return (
				<span className="flex items-center justify-center size-8 bg-gray-200 rounded-full text-gray-700 text-nowrap">
					{index + 1}
				</span>
			);
		}
		return null;
	};

	// Action Buttons Component
	const ActionButtons = () => {
		if (isOrderPreApproved) {
			return (
				<div className="flex flex-col justify-between gap-y-2">
					<div className="size-5 rounded-sm border-1 flex items-center justify-center transition-all bg-mint-700/50 border-transparent text-white">
						<CheckIcon />
					</div>
					<div className="size-5 rounded-sm border-1 flex items-center justify-center transition-all border-gray-300 bg-gray-100 text-gray-400">
						<XIcon />
					</div>
				</div>
			);
		}
		if (approvalDecisionHasBeenMade) {
			return (
				<div className="flex flex-col justify-between gap-y-2">
					<div className="size-5 rounded-sm border-1 flex items-center justify-center transition-all bg-mint-700 border-mint-700 text-white">
						<CheckIcon />
					</div>
				</div>
			);
		}

		if (deniedDecisionHasBeenMade) {
			return (
				<div className="flex flex-col justify-between gap-y-2">
					<div className="size-5 rounded-sm border-1 flex items-center justify-center transition-all bg-crimson-700 border-crimson-700 text-white">
						<XIcon />
					</div>
				</div>
			);
		}

		if (isPendingDecisionApproved) {
			return (
				<div className="flex flex-col justify-between gap-y-2">
					<div className="size-5 rounded-sm border-1 flex items-center justify-center transition-all bg-mint-700 border-mint-700 text-white">
						<CheckIcon />
					</div>
					<label className="relative cursor-pointer">
						<input
							type="radio"
							value="0"
							{...register(
								`${stage.valueKey}.actionButtonSelected`,
							)}
							className="peer sr-only"
						/>
						<div
							className={`${baseButtonClasses} peer-checked:bg-crimson-700 peer-checked:border-crimson-700 peer-checked:text-white`}
						>
							<XIcon />
						</div>
					</label>
				</div>
			);
		}

		if (isPendingDecisionDenied) {
			return (
				<div className="flex flex-col justify-between gap-y-2">
					<label
						className={`relative ${!isStageApprovalEnabled ? "cursor-not-allowed" : "cursor-pointer"}`}
					>
						<BaseToolTip
							isToolTipOn={!isStageApprovalEnabled}
							message="Needs an Assigned Manager"
						>
							<input
								type="radio"
								className="peer sr-only"
								value="1"
								disabled={!isStageApprovalEnabled}
								{...register(
									`${stage.valueKey}.actionButtonSelected`,
								)}
							/>
							<div
								className={`${baseButtonClasses} peer-checked:bg-mint-700 peer-checked:border-mint-700 peer-checked:text-white ${!isStageApprovalEnabled ? "opacity-50 cursor-not-allowed" : ""}`}
							>
								<CheckIcon />
							</div>
						</BaseToolTip>
					</label>
					<div className="size-5 rounded-sm border-1 flex items-center justify-center transition-all bg-crimson-700 border-crimson-700 text-white">
						<XIcon />
					</div>
				</div>
			);
		}

		if (isPendingDecisionEmpty) {
			return (
				<div className="flex flex-col gap-y-2">
					<label
						className={`relative ${!isStageApprovalEnabled ? "cursor-not-allowed" : "cursor-pointer"}`}
					>
						<BaseToolTip
							isToolTipOn={!isStageApprovalEnabled}
							message="Needs an Assigned Manager"
						>
							<input
								type="radio"
								className="peer sr-only"
								value="1"
								disabled={!isStageApprovalEnabled}
								{...register(
									`${stage.valueKey}.actionButtonSelected`,
								)}
							/>
							<div
								className={`${baseButtonClasses} peer-checked:bg-mint-700 peer-checked:border-mint-700 peer-checked:text-white ${!isStageApprovalEnabled ? "opacity-50 cursor-not-allowed" : ""}`}
							>
								<CheckIcon />
							</div>
						</BaseToolTip>
					</label>
					<label className="relative cursor-pointer">
						<input
							type="radio"
							value="0"
							{...register(
								`${stage.valueKey}.actionButtonSelected`,
							)}
							className="peer sr-only"
						/>
						<div
							className={`${baseButtonClasses} peer-checked:bg-crimson-700 peer-checked:border-crimson-700 peer-checked:text-white`}
						>
							<XIcon />
						</div>
					</label>
				</div>
			);
		}

		return null;
	};

	return (
		<>
			<button
				type="button"
				onClick={() => setSelectedStage(stage)}
				className="flex gap-x-4 w-full"
			>
				<div className="flex flex-col">
					<StageIndicator />
					<span className="font-omnes-regular text-sm text-[#6A7282]">
						{`Step ${index + 1}`}
					</span>
				</div>
				<div className="flex flex-col items-start">
					<span className="text-sm">{stage.label}</span>
					<span
						className={`text-sm text-[#6A7282] text-left ${assignedToValue?.uuid || stageAssignedUser ? "font-omnes-regular" : "italic"}`}
					>
						{assignedToName || stageAssignedUser || "Unassigned"}
					</span>
				</div>
			</button>
			<div className="flex flex-col justify-between">
				<ActionButtons />
			</div>
		</>
	);
};

export default ApprovalStageCard;
